import React, { useState } from "react";

import {
  Typography,
  TextField,
  FormControl,
  MenuItem,
  Select,
  Box ,
  Radio,
  FormControlLabel,
  Chip,
  OutlinedInput,
  InputBase,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export function EditableTextField({
  label,
  value,
  name,
  onChange,
  type = "text", // Dynamically handle input type
  isEdit = true,
  multiline = false, // Supports multiline input
}) {
  return (
    <div>
      <Typography
        variant="body1"
        color="initial"
        sx={{
          marginBottom: isEdit ? 1 : 0,
          color: "#646464",
          fontWeight: "500",
        }}
      >
        {label}
      </Typography>
      {isEdit ? (
        <TextField
          fullWidth
          size="medium"
          name={name}
          variant="outlined"
          value={value}
          onChange={onChange}
          multiline={multiline} // Handle multiline
          minRows={multiline ? 3 : undefined}
          type={type} // Dynamically handle input type
          sx={{
            color: "#313131",
            fontWeight: "500",
            marginBottom: "0px",
            "& .MuiInputBase-root": {
              height: multiline ? "auto" : "45px", // Adjust height for multiline
            },
            "& .MuiInputBase-input": {
              fontWeight: 500,
              padding: multiline ? "10px" : "5px", // Adjust padding for multiline
            },
          }}
          InputLabelProps={{
            shrink: type === "date", // Ensure proper label shrink for date input
          }}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{
            my: 0,
            padding: "10px 0",
            fontWeight: "500",
            color: "#313131",
          }}
        >
          {value}
        </Typography>
      )}
    </div>
  );
}

export function EditableSelectField({
  label,
  value,
  options = [{label:"Option1",value:"option1"},{label:"Option2",value:"option2"},{label:"Option3",value:"option3"}],
  name,
  onChange,
  isEdit = true,
}) {
  return (
    <div>
      <Typography
        variant="body1"
        color="initial"
        sx={{
          marginBottom: isEdit ? 1 : 0,
          color: "#646464",
          fontWeight: "500",
        }}
      >
        {label}
      </Typography>
      {isEdit ? (
        <FormControl
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "45px", // Set dropdown height
            },
            "& .MuiSelect-select": {
              fontWeight: 500,
              padding: "10px", // Adjust padding inside select
              color: "#313131",
            },
          }}
        >
          <Select
            name={name}
            value={value}
            onChange={onChange}
            variant="outlined"
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Typography
          variant="body1"
          sx={{
            my: 0,
            padding: "10px 0",
            fontWeight: "500",
            color: "#313131",
          }}
        >
          {value}
        </Typography>
      )}
    </div>
  );
}


export function EditableMultiSelectField({
  label,
  value = [], // Array of selected options
  options = [], // Array of available options
  name,
  onChange,
  isEdit = true,
}) {
  const handleSelectChange = (event) => {
    const {
      target: { value: selectedValue },
    } = event;
    // Ensure that selectedValue is always an array
    event.value=typeof selectedValue === "string" ? selectedValue.split(",") : selectedValue;
    onChange(event);
  };

  return (
    <div>
      <Typography
        variant="body1"
        color="initial"
        sx={{
          marginBottom: isEdit ? 1 : 0,
          color: "#646464",
          fontWeight: "500",
        }}
      >
        {label}
      </Typography>
      {isEdit ? (
        <FormControl
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: "45px", // Set dropdown minimum height
            },
            "& .MuiSelect-select": {
              fontWeight: 500,
              padding: "10px", // Adjust padding inside select
              color: "#313131",
            },
          }}
        >
          <Select
            name={name}
            multiple
            value={value}
            onChange={handleSelectChange}
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((val) => (
                  <Chip
                    key={val}
                    label={options.find((option) => option.value === val)?.label || val}
                    sx={{
                      backgroundColor: "#e8f4fd",
                      color: "#3178c6",
                      fontWeight: "500",
                    }}
                  />
                ))}
              </Box>
            )}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: 1 }}>
          {value.map((val) => (
            <Chip
              key={val}
              label={options.find((option) => option.value === val)?.label || val}
              sx={{
                backgroundColor: "#e8f4fd",
                color: "#3178c6",
                fontWeight: "500",
              }}
            />
          ))}
        </Box>
      )}
    </div>
  );
}


export function RadioOptions({
  label,
  options = [],
  value,
  onChange,
  otherValue,
  onOtherChange,
  isEdit = true,
}) {
  const [selectedValue, setSelectedValue] = useState(value || "");
  const [showOtherField, setShowOtherField] = useState(otherValue ? true : false);

  const handleRadioChange = (event) => {
    const selected = event.target.value;
    setSelectedValue(selected);
    if (selected === "other") {
      setShowOtherField(true);
      onChange(""); // Reset main value
    } else {
      setShowOtherField(false);
      onChange(selected); // Update main value
    }
  };

  return (
    <Box>
      <Typography
        variant="body1"
        sx={{
          marginBottom: isEdit ? 1 : 0,
          color: "#646464",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {isEdit ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginBottom: 1,
            flexWrap: "wrap",
          }}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio
                  checked={selectedValue === option.value}
                  onChange={handleRadioChange}
                  sx={{
                    "&.Mui-checked": {
                      color: "#1976d2", // Custom color for the selected radio button
                    },
                  }}
                />
              }
              label={option.label}
              sx={{
                margin: 0,
                color: "#313131",
                fontWeight: 500,
              }}
            />
          ))}
          <FormControlLabel
            value="other"
            control={
              <Radio
                checked={selectedValue === "other"}
                onChange={handleRadioChange}
                sx={{
                  "&.Mui-checked": {
                    color: "#1976d2",
                  },
                }}
              />
            }
            label="Other"
            sx={{
              margin: 0,
              color: "#313131",
              fontWeight: 500,
            }}
          />
        </Box>
      ) : (
        // Display selected value when isEdit is false
        <Typography
          variant="body1"
          sx={{
            color: "#313131",
            fontWeight: 500,
            padding: "10px 0",
          }}
        >
          {selectedValue === "other" && otherValue ? `Other: ${otherValue}` : getLabel(options, selectedValue)}
        </Typography>
      )}

      {isEdit && showOtherField && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              marginBottom: 1,
              color: "#646464",
              fontWeight: 500,
            }}
          >
            Other (Please specify)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder="Enter here"
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
              },
              "& .MuiInputBase-input": {
                fontWeight: 500,
                padding: "5px",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// Helper function to get the label for the selected value
function getLabel(options, selectedValue) {
  const selectedOption = options.find((option) => option.value === selectedValue);
  return selectedOption ? selectedOption.label : "";
}


export const SearchBar = ({ placeholder = "Search...", value, onChange, name = "search",sx }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#f9f9f9",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "4px 8px",
        width: "100%",
        boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
       
      }}
    >
      {/* Search Icon */}
      <IconButton disabled sx={{ padding: 0 }}>
        <SearchIcon sx={{ color: "#aaa" }} />
      </IconButton>

      {/* Input Field */}
      <InputBase
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        sx={{
          flex: 1,
          ml: 1,
          fontSize: "14px",
          color: "#333",
          ...sx
        }}
        inputProps={{ "aria-label": "search" }}
      />
    </Box>
  );
};